<script setup lang="ts">
import type { ProductItem } from '@/js/types/catalog.ts';
import { getProductsByQuery } from '@/js/api/catalog.ts';
import { getLocaleValue } from '@/js/helper/locale.ts';
import { ERROR_REQUEST_TEXT, showErrorNotify } from '@/js/notification.ts';
import { classname, formatPrice } from '@/js/utils.ts';
import BaseButton from '@/js/vue/base/base-button.vue';
import BaseIcon from '@/js/vue/base/base-icon.vue';
import BaseLoader from '@/js/vue/base/base-loader.vue';
import ProductSearchItem from '@/js/vue/products-search/product-search-item.vue';
import { onClickOutside, watchDebounced } from '@vueuse/core';
import { shallowRef, useTemplateRef } from 'vue';

const DEBOUNCE = 600;
const MAX_WAIT = 3000;
const cn = classname('products-search');
const target = useTemplateRef<HTMLElement>('target');

const query = shallowRef('');
const products = shallowRef<ProductItem[] | null>(null);
const popupShown = shallowRef(false);
const isLoading = shallowRef(false);

watchDebounced(
  query,
  async (nextQuery) => {
    if (nextQuery.length === 0) {
      products.value = null;
      return;
    }
    isLoading.value = true;
    const { data, error } = await getProductsByQuery(nextQuery);
    products.value = data || [];
    if (error) {
      showErrorNotify({ text: ERROR_REQUEST_TEXT });
    }
    isLoading.value = false;
    popupShown.value = true;
  },
  {
    debounce: DEBOUNCE,
    maxWait: MAX_WAIT,
  },
);

onClickOutside(target, () => (popupShown.value = false));
</script>

<template>
  <div ref="target" :class="cn()">
    <label :class="cn('group')">
      <input
        v-model="query"
        type="search"
        :class="cn('control')"
        :placeholder="getLocaleValue('stores_modal_search_placeholder')"
        @focus="popupShown = true"
      >
      <BaseButton :class="cn('btn')">
        <BaseIcon type="main" icon="search" :width="16" :height="16" />
      </BaseButton>
    </label>
    <div
      v-if="Array.isArray(products) && popupShown"
      :class="cn('container')"
    >
      <BaseLoader v-if="isLoading" size="sm" :class="cn('loader')" />
      <ProductSearchItem
        v-for="product in products"
        v-else-if="products.length > 0"
        :key="product.id"
        :name="product.title"
        :image="product.image.url"
        :url="product.url"
        :sku="product.sku"
        :price="product.price ? formatPrice(product.price) : getLocaleValue('product_request_price')"
        :old-price="product.oldPrice ? formatPrice(product.oldPrice) : null"
      />
      <div v-else :class="cn('empty')">
        {{ getLocaleValue('nothing_found') }}
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.products-search {
  position: relative;

  &__control {
    width: 100%;
    height: 50px;
    padding: 12px 60px 12px 18px;
    font-size: 14px;
    background-color: #f5f8fe;
    border: 1px solid #ced2df;
    border-radius: 8px;

    &::placeholder {
      color: #a4a4a4;
    }
  }

  &__btn {
    position: absolute;
    top: 5px;
    right: 5px;
    bottom: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    color: #fff;
    background: var(--light-primary-color);
    border-radius: 5px;

    svg {
      fill: currentcolor;
    }
  }

  &__container {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    width: 100%;
    max-width: 440px;
    max-height: 672px;
    padding-bottom: 8px;
    overflow-y: auto;
    background-color: #f5f8fe;
    border: 1px solid #ced2df;
  }

  &__loader,
  &__empty {
    margin: 16px;
  }

  @media (width < 1200px) {
    &__container {
      top: auto;
      right: 0;
      bottom: 100%;
      left: 0;
      max-width: 100%;
      max-height: min(60vh, calc(100dvh - var(--header-actions-height) - 120px));
    }
  }

  @media (width <= 992px) {
    padding: 12px;

    &__control {
      height: 38px;
      padding-inline: 10px 40px;
    }

    &__btn {
      top: 15px;
      right: 15px;
      bottom: 3px;
      width: 32px;
      height: 32px;
    }
  }
}
</style>
